import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, ArrowRight } from "lucide-react";
import AuthLayout from "../components/AuthLayout";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    try {
      await login(email, password, "customer");
      toast({ title: "Welcome back", description: "You're signed in." });
      const from = location.state?.from?.pathname || "/customer-dashboard";
      navigate(from, { replace: true });
    } catch (err) {
      toast({
        title: "Sign-in failed",
        description: err?.message?.replace("Firebase: ", "") || "Please check your credentials.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      variant="customer"
      title="Sign in to your account"
      subtitle="Track shipments, view invoices and re-order routes."
      footer={
        <>
          Don't have an account?{" "}
          <Link to="/register" className="text-[#f59321] font-semibold hover:underline">
            Create one
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-5">
        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-[#0f2847] font-semibold">Email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="h-12 pl-10 rounded-xl border-slate-200 focus:border-[#f59321] focus:ring-[#f59321]"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="password" className="text-[#0f2847] font-semibold">Password</Label>
            <Link to="/forgot-password" className="text-xs font-semibold text-[#f59321] hover:underline">
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              id="password"
              type={show ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="h-12 pl-10 pr-12 rounded-xl border-slate-200 focus:border-[#f59321] focus:ring-[#f59321]"
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              aria-label="Toggle password"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            >
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
          <Checkbox checked={remember} onCheckedChange={setRemember} className="data-[state=checked]:bg-[#f59321] data-[state=checked]:border-[#f59321]" />
          Remember me for 30 days
        </label>
        <Button
          type="submit"
          disabled={submitting}
          className="btn-lift w-full bg-[#f59321] hover:bg-[#e07f10] text-white font-semibold rounded-full h-12 shadow-md disabled:opacity-60"
        >
          {submitting ? "Signing in…" : "Sign in"}
          <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
        <div className="text-center text-xs text-slate-500">
          Secured by Firebase Authentication
        </div>
      </form>
    </AuthLayout>
  );
}
